import { css } from "@compiled/react";

import Avatar from "@atlaskit/avatar";
import EpicIcon from "@atlaskit/icon/core/epic";
import PriorityHighIcon from "@atlaskit/icon/core/priority-high";
import PriorityLowIcon from "@atlaskit/icon/core/priority-low";
import PriorityMediumIcon from "@atlaskit/icon/core/priority-medium";
import TaskIcon from "@atlaskit/icon/core/task";
import Lozenge from "@atlaskit/lozenge";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import {
    tableBorderNotLastCellStyles,
    tableCellStyles
} from "./shared-styles.ts";

enum StatusDisplay {
    ToDo = "TO DO",
    InProgress = "IN PROGRESS",
    Complete = "COMPLETE"
}

enum PriorityDisplay {
    Low = "Low",
    Medium = "Medium",
    High = "High"
}

const inlineRowStyles = css({
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem"
});

const placeHolderBarStyles = {
    root: css({
        height: "0.5rem",
        backgroundImage:
            // eslint-disable-next-line local/ensure-wac-token-usage
            "linear-gradient(to right, #1F69DA 7%, #C16CD4 29%, #F5A117 65%, #79AE44 85%)",
        opacity: 0.2,
        display: "inline-block"
    }),
    narrow: css({
        width: "5rem"
    }),
    wide: css({
        width: "28.125rem"
    })
};

const lozengeStyles = {
    toDo: {
        backgroundColor: wacToken("color.neutral.light300"),
        color: wacToken("color.neutral.light1000")
    },
    inProgress: {
        backgroundColor: wacToken("color.extended.blue300"),
        color: wacToken("color.neutral.light1000")
    },
    complete: {
        backgroundColor: wacToken("color.extended.lime300"),
        color: wacToken("color.neutral.light1000")
    },
    dateContainer: css({
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> span": {
            borderColor: wacToken("color.neutral.light400"),
            borderStyle: "solid",
            borderWidth: "1px"
        }
    }),
    date: {
        backgroundColor: wacToken("color.neutral.light0"),
        color: wacToken("color.neutral.light1000")
    }
};

const avatarStyles = css({
    display: "flex",
    alignItems: "center"
});

const summaryStyles = css({
    textOverflow: "ellipsis"
});

const epicSummaryStyles = css({
    fontWeight: 700
});

const tableBorderNotLastRowStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    borderBlockEndColor: "rgba(11, 18, 40, 0.14)",
    borderBlockEndStyle: "solid",
    borderBlockEndWidth: "1px"
});

function renderStatusLozenge(status?: TableRowProps["status"]) {
    switch (status) {
        case "to do":
            return (
                <Lozenge style={lozengeStyles.toDo}>
                    {StatusDisplay.ToDo}
                </Lozenge>
            );
        case "in progress":
            return (
                <Lozenge style={lozengeStyles.inProgress}>
                    {StatusDisplay.InProgress}
                </Lozenge>
            );
        case "complete":
            return (
                <Lozenge style={lozengeStyles.complete}>
                    {StatusDisplay.Complete}
                </Lozenge>
            );
        default:
            return (
                <div
                    css={[
                        placeHolderBarStyles.root,
                        placeHolderBarStyles.narrow
                    ]}
                />
            );
    }
}

function renderDateLozenge(dueDate?: Date) {
    if (!dueDate)
        return (
            <div
                css={[placeHolderBarStyles.root, placeHolderBarStyles.narrow]}
            />
        );

    return (
        <span css={lozengeStyles.dateContainer}>
            <Lozenge style={lozengeStyles.date}>
                {formatDate(dueDate).toLocaleUpperCase()}
            </Lozenge>
        </span>
    );
}

function renderPriority(priority?: TableRowProps["priority"]) {
    switch (priority) {
        case "low":
            return (
                <span css={inlineRowStyles}>
                    <PriorityLowIcon
                        label="low priority"
                        color={token("color.icon.accent.blue")}
                    />
                    <span>{PriorityDisplay.Low}</span>
                </span>
            );
        case "medium":
            return (
                <span css={inlineRowStyles}>
                    <PriorityMediumIcon
                        label="medium priority"
                        color={token("color.icon.accent.yellow")}
                    />
                    <span>{PriorityDisplay.Medium}</span>
                </span>
            );
        case "high":
            return (
                <span css={inlineRowStyles}>
                    <PriorityHighIcon
                        label="high priority"
                        color={token("color.icon.accent.red")}
                    />
                    <span>{PriorityDisplay.High}</span>
                </span>
            );
        default:
            return (
                <div
                    css={[
                        placeHolderBarStyles.root,
                        placeHolderBarStyles.narrow
                    ]}
                />
            );
    }
}

function renderAssignee(assignee?: string, assigneeImgSrc?: string) {
    if (assignee && assigneeImgSrc) {
        return (
            <div css={[avatarStyles, inlineRowStyles]}>
                <Avatar
                    appearance="circle"
                    src={assigneeImgSrc}
                    size="small"
                    name={assignee}
                />
                <span>{assignee}</span>
            </div>
        );
    } else if (assignee) {
        return assignee;
    } else {
        return (
            <div
                css={[placeHolderBarStyles.root, placeHolderBarStyles.narrow]}
            />
        );
    }
}

export interface TableRowProps {
    type: "epic" | "task";
    summary: string;
    status: "to do" | "in progress" | "complete";
    dueDate: Date;
    priority: "low" | "medium" | "high";
    assignee: string;
    assigneeImgSrc?: string;
    isLastRow: boolean;
}

function formatDate(date: Date, locale?: string): string {
    const loc =
        locale ||
        (typeof navigator !== "undefined" ? navigator.language : "en-US");
    const dtf = new Intl.DateTimeFormat(loc, {
        month: "short",
        day: "numeric",
        year: "numeric"
    });

    const parts = dtf.formatToParts(date);

    const getOrdinal = (n: number) => {
        const rem10 = n % 10;
        const rem100 = n % 100;
        if (rem10 === 1 && rem100 !== 11) return `${n}st`;
        if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
        if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
        return `${n}th`;
    };

    const isEnglish = /^en(-|$)/i.test(loc);

    const withOrdinal = parts.map((p) => {
        if (p.type === "day" && isEnglish) {
            const num = Number(p.value);
            return {
                ...p,
                value: Number.isFinite(num) ? getOrdinal(num) : p.value
            } as Intl.DateTimeFormatPart;
        }
        return p;
    });

    const formatted = withOrdinal.map((p) => p.value).join("");
    return formatted.toLocaleUpperCase(loc);
}

export const TableRow = ({
    type,
    summary,
    status,
    dueDate,
    priority,
    assignee,
    assigneeImgSrc,
    isLastRow
}: Partial<TableRowProps>) => {
    const summaryIcon =
        type === "epic" ? (
            <EpicIcon
                label=""
                size="small"
                color={token("color.icon.accent.purple")}
            />
        ) : (
            <TaskIcon
                label=""
                size="small"
                color={token("color.icon.accent.blue")}
            />
        );

    const summaryCell = summary || (
        <div css={[placeHolderBarStyles.root, placeHolderBarStyles.wide]} />
    );

    return (
        <tr>
            <td
                css={[
                    css(tableCellStyles),
                    summaryStyles,
                    type === "epic" && epicSummaryStyles,
                    css(tableBorderNotLastCellStyles),
                    !isLastRow && tableBorderNotLastRowStyles
                ]}
            >
                <span css={inlineRowStyles}>
                    <span>{summaryIcon}</span>
                    <span>{summaryCell}</span>
                </span>
            </td>
            <td
                css={[
                    css(tableCellStyles),
                    css(tableBorderNotLastCellStyles),
                    !isLastRow && tableBorderNotLastRowStyles
                ]}
            >
                {renderStatusLozenge(status)}
            </td>
            <td
                css={[
                    css(tableCellStyles),
                    css(tableBorderNotLastCellStyles),
                    !isLastRow && tableBorderNotLastRowStyles
                ]}
            >
                {renderDateLozenge(dueDate)}
            </td>
            <td
                css={[
                    css(tableCellStyles),
                    css(tableBorderNotLastCellStyles),
                    !isLastRow && tableBorderNotLastRowStyles
                ]}
            >
                {renderPriority(priority)}
            </td>
            <td
                css={[
                    css(tableCellStyles),
                    !isLastRow && tableBorderNotLastRowStyles
                ]}
            >
                {renderAssignee(assignee, assigneeImgSrc)}
            </td>
        </tr>
    );
};
